/**
 * Centralized error formatting utilities for user-facing error messages.
 *
 * Extends the error classification in ./errors.ts with human-readable formatting.
 * All error codes are preserved as full 5-digit numbers (e.g. 40100, 50000).
 */

import { GraphQLError } from 'graphql';
import { getErrorCodeGroup } from './errors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FormattedError {
  /** Human-readable error reason */
  userMessage: string;
  /** Full 5-digit backend error code, e.g. 40100, 50000 */
  errorCode?: number;
  /** First 8 chars of the request_id from error extensions */
  traceId?: string;
  /** userMessage + suffix like " (Error 40100, Ref: 54719c13)" when available */
  display: string;
}

export type BatchStatus = {
  status: 'fulfilled' | 'rejected';
  reason?: string;
  categoryKey: string;
  itemLabels: string[];
};

// ---------------------------------------------------------------------------
// GraphQL error formatting
// ---------------------------------------------------------------------------

function buildDisplaySuffix(errorCode?: number, traceId?: string): string {
  const parts: string[] = [];
  if (errorCode != null) parts.push(`Error ${errorCode}`);
  if (traceId) parts.push(`Ref: ${traceId}`);
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}

/**
 * Extracts the human-readable portion from a backend error message.
 * Backend messages may include structured prefixes and metadata, e.g.:
 *   "Error code: 41021, message: This hive is already assigned... Metadata: serial: abc123"
 * This strips the "Error code: ..., message:" prefix and "Metadata: ..." suffix.
 */
export function cleanBackendMessage(raw: string): string {
  let msg = raw;
  // Strip "Error code: XXXXX, message: " prefix
  const msgMatch = msg.match(/^Error code:\s*\d+,\s*message:\s*(.+)/i);
  if (msgMatch) {
    msg = msgMatch[1];
  }
  // Strip " Metadata: ..." suffix
  const metaIdx = msg.search(/\s*Metadata:\s/i);
  if (metaIdx !== -1) {
    msg = msg.slice(0, metaIdx);
  }
  const cleaned = msg.trim();
  return cleaned || 'An error occurred';
}

export function formatGraphQLError(error: GraphQLError): FormattedError {
  const code = error.extensions?.code;
  const errorCode = typeof code === 'number' ? code : undefined;
  const rawRequestId = error.extensions?.request_id;
  const requestId = typeof rawRequestId === 'string' ? rawRequestId : undefined;
  const traceId = requestId ? requestId.slice(0, 8) : undefined;

  const group = getErrorCodeGroup(error);
  let userMessage: string;

  switch (group) {
    case 400:
      // Bad request: pass through backend message (contains actionable detail)
      userMessage = cleanBackendMessage(error.message);
      break;
    case 401:
    case 403:
      userMessage = 'Authentication error. Please refresh the page and try again.';
      break;
    case 404:
      userMessage = 'The requested resource was not found.';
      break;
    case 409:
      userMessage =
        'A conflict occurred. The resource may have been modified. Please refresh and try again.';
      break;
    case 429:
      userMessage = 'Too many requests. Please wait a moment and try again.';
      break;
    case 500:
      if (error.message) {
        console.error('Server error detail:', error.message);
      }
      userMessage =
        'An unexpected server error occurred. Please try again, or contact support if the issue persists.';
      break;
    default:
      if (group === null) {
        // No numeric code (e.g. string codes like "UNAUTHENTICATED") — show safe generic message
        if (error.message) {
          console.warn('Unclassified GraphQL error (no numeric code):', error.message);
        }
        userMessage = 'Something went wrong. Please try again.';
      } else {
        // Unrecognized numeric group — pass through cleaned backend message
        userMessage = error.message
          ? cleanBackendMessage(error.message)
          : 'Something went wrong. Please try again.';
      }
      break;
  }

  return {
    userMessage,
    errorCode,
    traceId,
    display: userMessage + buildDisplaySuffix(errorCode, traceId),
  };
}

// ---------------------------------------------------------------------------
// Network error formatting
// ---------------------------------------------------------------------------

export function formatNetworkError(error: unknown): string {
  if (error == null) {
    console.warn('formatNetworkError called with null/undefined error');
    return 'A network error occurred. Please check your connection and try again.';
  }
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && 'message' in error
      ? String((error as Record<string, unknown>).message)
      : '';
  const lower = msg.toLowerCase();

  if (lower.includes('failed to fetch') || lower.includes('networkerror')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  if (lower.includes('timeout') || lower.includes('timed out')) {
    return 'The request timed out. Please try again.';
  }
  if (msg) {
    console.warn('Unrecognized network error:', msg);
  }
  return 'A network error occurred. Please check your connection and try again.';
}

// ---------------------------------------------------------------------------
// HTTP status formatting (for non-GraphQL API calls)
// ---------------------------------------------------------------------------

export function formatHttpError(status: number, serverMessage?: string): string {
  if (status === 401 || status === 403) {
    return 'Authentication error. Please refresh and try again.';
  }
  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (status === 400 || status === 422) {
    return serverMessage
      ? cleanBackendMessage(serverMessage)
      : 'Invalid request. Please check your input and try again.';
  }
  if (status >= 500) {
    return 'Server error. Please try again in a few minutes.';
  }
  return 'Something went wrong. Please try again.';
}

// ---------------------------------------------------------------------------
// Batch save formatting (for Studio save operations)
// ---------------------------------------------------------------------------

/**
 * Format multiple GraphQL errors from a single batch mutation response.
 * Deduplicates messages and combines trace refs.
 */
export function formatSaveErrors(errors: ReadonlyArray<GraphQLError>): FormattedError {
  const formatted = errors.map(formatGraphQLError);

  // Deduplicate user messages while preserving order
  const seen = new Set<string>();
  const uniqueMessages: string[] = [];
  for (const f of formatted) {
    if (!seen.has(f.userMessage)) {
      seen.add(f.userMessage);
      uniqueMessages.push(f.userMessage);
    }
  }

  const traceIds = formatted.map((f) => f.traceId).filter((t): t is string => !!t);
  const uniqueTraceIds = [...new Set(traceIds)];

  // Intentionally use only the first error code in the display suffix — the userMessage
  // already contains all distinct messages, so repeating every code would add noise.
  const errorCodes = formatted.map((f) => f.errorCode).filter((c): c is number => c != null);
  const firstCode = errorCodes[0];

  const userMessage = uniqueMessages.join('; ');

  // Build inline suffix matching buildDisplaySuffix style: (Error XXXXX, Ref: abcd1234)
  const suffixParts: string[] = [];
  if (firstCode != null) suffixParts.push(`Error ${firstCode}`);
  if (uniqueTraceIds.length > 0) suffixParts.push(`Ref: ${uniqueTraceIds.join(', ')}`);
  const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(', ')})` : '';

  return {
    userMessage,
    errorCode: firstCode,
    traceId: uniqueTraceIds[0],
    display: userMessage + suffix,
  };
}

// ---------------------------------------------------------------------------
// Item label extraction
// ---------------------------------------------------------------------------

interface BaseStateForLookup {
  rooms?: Array<{ id: string; name?: string }>;
  sensors?: Array<{ id: string; macAddress?: string; name?: string }>;
  hives?: Array<{ id: string; serial_number?: string; name?: string }>;
  zones?: Array<{ id: string; name?: string }>;
  floorplans?: Array<{ id: string; name?: string }>;
}

/**
 * A batch item from mutation variables. Intentionally loose (Record<string, unknown> | string)
 * because StudioUpdateAllMutationVariables has many distinct input types (CreateHiveInput,
 * UpdateSensorInput, DeleteFloorPlanInput, plain string IDs, etc.) and the batch system
 * processes them generically. Property access is narrowed with typeof guards in getItemLabel.
 */
export type BatchItem = Record<string, unknown> | string;

/**
 * Extracts user-friendly identifiers from batch mutation variables.
 * For removed items, looks up identifiers from base state.
 */
export function getItemLabel(
  categoryKey: string,
  items: BatchItem[],
  baseState?: BaseStateForLookup,
): string[] {
  const objs = items.filter((i): i is Record<string, unknown> => typeof i !== 'string');

  switch (categoryKey) {
    case 'newHives':
    case 'hives':
      return objs
        .map((item) => {
          const sn = typeof item.serial_number === 'string' ? item.serial_number : undefined;
          const name = typeof item.name === 'string' ? item.name : undefined;
          return sn ? `SN: ${sn}` : name || null;
        })
        .filter((label): label is string => label != null);

    case 'newSensors':
    case 'sensors':
      return objs
        .map((item) => {
          const mac = typeof item.mac_address === 'string' ? item.mac_address : undefined;
          const name = typeof item.name === 'string' ? item.name : undefined;
          return mac ? `MAC: ${mac}` : name || null;
        })
        .filter((label): label is string => label != null);

    case 'newRooms':
    case 'rooms':
    case 'newZones':
    case 'zones':
    case 'floorPlans':
      return objs
        .map((item) => {
          const name = typeof item.name === 'string' ? item.name : undefined;
          return name ? `"${name}"` : null;
        })
        .filter((label): label is string => label != null);

    case 'removedRooms':
      return lookupRemovedItems(items, baseState?.rooms, (r) => (r.name ? `"${r.name}"` : null));

    case 'removedSensors':
      return lookupRemovedItems(items, baseState?.sensors, (s) =>
        s.macAddress ? `MAC: ${s.macAddress}` : s.name || null,
      );

    case 'removedHives':
      return lookupRemovedItems(items, baseState?.hives, (h) =>
        h.serial_number ? `SN: ${h.serial_number}` : h.name || null,
      );

    case 'removedZones':
      return lookupRemovedItems(items, baseState?.zones, (z) => (z.name ? `"${z.name}"` : null));

    case 'removedFloorplans':
      return lookupRemovedItems(items, baseState?.floorplans, (fp) =>
        fp.name ? `"${fp.name}"` : null,
      );

    default:
      return [];
  }
}

function lookupRemovedItems<T extends { id: string }>(
  itemIds: BatchItem[],
  baseItems: T[] | undefined,
  format: (item: T) => string | null,
): string[] {
  if (!baseItems) return [];
  return itemIds
    .map((idOrObj) => {
      // Items are either string IDs or objects. removedFloorplans passes objects
      // with floor_plan_id (from DeleteFloorPlanInput) rather than plain string IDs.
      const rawId = typeof idOrObj === 'string' ? idOrObj : idOrObj?.floor_plan_id ?? idOrObj?.id;
      const id = typeof rawId === 'string' ? rawId : rawId != null ? String(rawId) : undefined;
      const found = id ? baseItems.find((b) => b.id === id) : undefined;
      return found ? format(found) : null;
    })
    .filter((label): label is string => label != null);
}

// ---------------------------------------------------------------------------
// Category label mapping
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, [string, string, string]> = {
  //                                     action   singular  plural
  newRooms: ['add', 'room', 'rooms'],
  newZones: ['add', 'zone', 'zones'],
  newSensors: ['add', 'sensor', 'sensors'],
  newHives: ['add', 'hive', 'hives'],
  rooms: ['update', 'room', 'rooms'],
  zones: ['update', 'zone', 'zones'],
  sensors: ['update', 'sensor', 'sensors'],
  hives: ['update', 'hive', 'hives'],
  floorPlans: ['update', 'floor plan', 'floor plans'],
  removedRooms: ['remove', 'room', 'rooms'],
  removedSensors: ['remove', 'sensor', 'sensors'],
  removedZones: ['remove', 'zone', 'zones'],
  removedHives: ['remove', 'hive', 'hives'],
  removedFloorplans: ['remove', 'floor plan', 'floor plans'],
};

export function getCategoryLabel(categoryKey: string, count?: number): string {
  const entry = CATEGORY_LABELS[categoryKey];
  if (!entry) return categoryKey;
  const [action, singular, plural] = entry;
  return `${action} ${count === 1 ? singular : plural}`;
}

// ---------------------------------------------------------------------------
// Batch save result formatting
// ---------------------------------------------------------------------------

/**
 * Builds the full save error message from enriched batch status entries.
 * Only lists what failed — no "Succeeded" section.
 */
export function formatBatchSaveResult(saveStatus: BatchStatus[]): string {
  const failed = saveStatus.filter((s) => s.status === 'rejected');
  if (failed.length === 0) return '';

  const allFailed = saveStatus.every((s) => s.status === 'rejected');
  const header = allFailed ? 'Save failed.' : 'Save partially completed.';

  const failureLines: string[] = [];

  for (const entry of failed) {
    const count = entry.itemLabels.length;
    const categoryLabel = getCategoryLabel(entry.categoryKey, count || undefined);
    const itemsStr = count > 0 ? ` (${entry.itemLabels.join(', ')})` : '';
    const reason = entry.reason || 'Unknown error';
    failureLines.push(`Failed to ${categoryLabel}${itemsStr}: ${reason}`);
  }

  // Single failure: one line. Multiple failures: header on its own line.
  if (failureLines.length === 1) {
    return `${header} ${failureLines[0]}`;
  }
  return [header, ...failureLines].join('\n');
}

// ---------------------------------------------------------------------------
// Operation error formatting (for simple CRUD operations)
// ---------------------------------------------------------------------------

/**
 * Extracts a sanitized, user-facing reason string from any error shape.
 * Handles Apollo errors (graphQLErrors, networkError), Error instances, and strings.
 * For graphQLErrors, deduplicates and aggregates via formatSaveErrors (multiple errors
 * collapse to a single deduplicated message + one shared error code).
 *
 * Use this when you only need the reason fragment (e.g. for toast body text).
 * Use {@link formatOperationError} instead when you want a full "Failed to {op}: {reason}"
 * string and need each GraphQL error to carry its own individual error code.
 */
export function extractErrorReason(error: unknown): string {
  if (error && typeof error === 'object') {
    if (
      'graphQLErrors' in error &&
      Array.isArray(error.graphQLErrors) &&
      error.graphQLErrors.length > 0
    ) {
      return formatSaveErrors(error.graphQLErrors).display;
    }

    if ('networkError' in error && error.networkError) {
      return formatNetworkError(error.networkError);
    }

    if (error instanceof Error) {
      return cleanBackendMessage(error.message);
    }
  }

  if (typeof error === 'string') {
    return cleanBackendMessage(error);
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Wraps any error into a "Failed to {operation}: {reason}" format.
 * Handles GraphQL errors, network errors, Error instances, and strings.
 * For graphQLErrors, formats each error individually (preserving per-error codes).
 */
export function formatOperationError(operation: string, error: unknown): string {
  // For graphQLErrors, preserve per-error display (each with its own error code)
  if (
    error &&
    typeof error === 'object' &&
    'graphQLErrors' in error &&
    Array.isArray(error.graphQLErrors) &&
    error.graphQLErrors.length > 0
  ) {
    const formatted = error.graphQLErrors.map((e: GraphQLError) => formatGraphQLError(e).display);
    return `Failed to ${operation}: ${formatted.join('; ')}`;
  }
  return `Failed to ${operation}: ${extractErrorReason(error)}`;
}
